import React from 'react';

import {Group} from '@pb/kit-component-group';
import {Tooltip} from '@pb/kit-component-popups';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {Button} from '../button';

import styles from './compound-button.module.styl';

import type {ButtonProps} from '../button';
import type {ReactNode} from 'react';


const classes = hashedClasses(styles);
const classNames = withCssModule(styles);

interface SecondaryButtonProps extends ButtonProps {
    /**
     * Tooltip text displayed on hover of the secondary button. When set, wraps
     * the secondary button in a `<Tooltip>` and sets `aria-label` to this value.
     * Prefer `renderSecondaryButton` to avoid introducing a Tooltip dependency.
     *
     * @deprecated It will be removed in 6 months 04-31-2026. Use renderSecondaryButton instead.
     */
    tooltip?: string,
}

export type CompoundButtonProps = {
    /**
     * Optional separator element rendered between the primary and secondary
     * buttons. Typically a vertical divider line. Only meaningful with flat
     * button variants.
     */
    divider?: ReactNode,
    /**
     * Props forwarded to the primary (main-action) `Button`. The `variant` prop
     * from `CompoundButtonProps` is merged in automatically.
     */
    primaryButton: ButtonProps,
    /**
     * Render prop for the secondary (disclosure/dropdown) button. Receives the
     * computed `ButtonProps` (including merged `variant` and class names) and
     * must return a React element. Use this instead of the deprecated `tooltip`
     * prop on `secondaryButton` to keep the Button package free of Tooltip.
     */
    renderSecondaryButton?: (props: ButtonProps) => ReactNode,
    /**
     * Props forwarded to the secondary (disclosure/dropdown) `Button`. The
     * `variant` prop from `CompoundButtonProps` is merged in automatically.
     */
    secondaryButton: SecondaryButtonProps,
    /**
     * Visual style preset shared by both buttons. Accepts the same values as
     * `ButtonProps.variant` (`"Type.PRIMARY"`, `"Type.SECONDARY"`, etc.).
     */
    variant: ButtonProps['variant'],
};

/**
 * Two-button combination where a primary action sits beside a secondary
 * disclosure button (e.g. "Save" + "▼ More options").
 *
 * Both buttons share the same `variant`. The secondary button can render a
 * custom element via `renderSecondaryButton` — preferred over the deprecated
 * `tooltip` prop on `secondaryButton`. An optional `divider` node is rendered
 * between the two buttons.
 *
 * ## Accessibility
 * The secondary button's `aria-label` defaults to the deprecated `tooltip`
 * text. When using `renderSecondaryButton`, set `aria-label` explicitly on the
 * button element you return.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/buttons-button-compound-button--documentation}
 */

export const CompoundButton = (props: CompoundButtonProps) => {
    const {
        divider,
        primaryButton,
        renderSecondaryButton,
        secondaryButton,
        variant,
    } = props;

    const primaryButtonClasses = classNames('compound-button__primary', primaryButton?.className);
    const secondaryButtonClasses = classNames('compound-button__secondary', secondaryButton?.className);

    const {tooltip, ...otherSBProps} = secondaryButton;

    const secondaryButtonProps = {
        ...otherSBProps,
        variant,
        'aria-label': tooltip || secondaryButton['aria-label'],
        'className': secondaryButtonClasses,
    };

    const renderSecondaryButtonComponent = () => {
        const button = renderSecondaryButton
            ? renderSecondaryButton(secondaryButtonProps)
            : <Button {...secondaryButtonProps}/>;

        if (!tooltip) {
            return button;
        }

        return (
            <Tooltip
                anchor={({anchorRef, getAnchorProps}) => (
                    <div ref={anchorRef} {...getAnchorProps()}>
                        {button}
                    </div>
                )}>
                {tooltip}
            </Tooltip>
        );
    };

    return (
        <Group
            gap="none"
            className={classes['compound-button']}>
            <Button
                {...primaryButton}
                variant={variant}
                className={primaryButtonClasses}
            />
            {divider}
            {renderSecondaryButtonComponent()}
        </Group>
    );
};

CompoundButton.displayName = 'CompoundButton';
